import React, { useState, useEffect, createContext } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../config/axios-config";
import routes from "../config/routes-config";

export const LoginContext = createContext({
  userAuthStatus: false,
  user: null,
  departments: [],
  domains: [],
  activeTags: [],
  setUserAuthStatus: () => {},
  setUser: () => {},
  setDepartments: () => {},
  handleUserLogin: () => {},
  handleUserLogout: () => {},
  getAllDepartments: () => {},
  getAllDomains: () => {},
  getAllActiveTags: () => {},
});

export const LoginProvider = (props) => {
  const [userAuthStatus, setUserAuthStatus] = useState(false);
  const [user, setUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [domains, setDomains] = useState([]);
  const [activeTags, setActiveTags] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (userAuthStatus === true && user !== null) {
      getAllDepartments(
        routes.common.departmentsUrl,
        user.token,
        setDepartments
      );

      getAllDomains(routes.common.activeDomainsUrl, user.token, setDomains);

      getAllActiveTags(routes.common.activeTagsUrl, user.token, setActiveTags);
    }
  }, [userAuthStatus, user]);

  async function handleUserLogin(url, userPayload, setErrors) {
    try {
      const response = await axiosInstance.post(url, userPayload);
      const userInfo = await response.data;

      sessionStorage.setItem("auth", JSON.stringify(userInfo));

      setUserAuthStatus(true);
      setUser(userInfo);

      if (userPayload.role === "student") {
        navigate("/student/all-projects", { replace: true });
      } else if (userPayload.role === "teacher") {
        navigate("/teacher/ongoing-projects", { replace: true });
      } else if (userPayload.role === "admin") {
        navigate("/admin/dashboard", { replace: true });
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setErrors({ username: `This ${userPayload.role} doesn't exist` });
      } else if (err.response && err.response.status === 406) {
        setErrors({ password: "You have entered a wrong password" });
      }
    }
  }

  async function getAllDepartments(url, jwtToken, setDepartments) {
    try {
      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const departmentList = await response.data;

      setDepartments(departmentList);
    } catch (err) {
      // console.log(err);
    }
  }

  async function getAllDomains(url, jwtToken, setDomains) {
    try {
      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const data = await response.data;

      const domainsList = [];
      for (const element of data) {
        domainsList.push(element.domainName);
      }

      setDomains(domainsList);
    } catch (err) {
      // console.log(err);
    }
  }

  async function getAllActiveTags(url, jwtToken, setActiveTags) {
    try {
      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const data = await response.data;

      const activeTagsList = [];
      for (const element of data) {
        activeTagsList.push(element.tagName);
      }

      setActiveTags(activeTagsList);
    } catch (err) {
      // console.log(err);
    }
  }

  const handleUserLogout = () => {
    sessionStorage.clear();

    setUser(null);

    setUserAuthStatus(false);

    navigate("/", { replace: true });
  };

  const contextValue = {
    userAuthStatus,
    user,
    departments,
    domains,
    activeTags,
    setUserAuthStatus,
    setUser,
    setDepartments,
    handleUserLogin,
    handleUserLogout,
    getAllDepartments,
    getAllDomains,
    getAllActiveTags,
  };

  return (
    <LoginContext.Provider value={contextValue}>
      {props.children}
    </LoginContext.Provider>
  );
};
